// ============================================================================
// Purchase Flow Types
// ============================================================================

export type PurchaseStage =
  | 'vendor_select'
  | 'item_select'
  | 'price_confirm'
  | 'delivery_schedule'
  | 'iqc_rule'
  | 'receiving'
  | 'invoice_input'
  | 'ap_completion'
  | 'close';

export type FlowStatus = 'active' | 'paused' | 'completed' | 'cancelled';

export const STAGE_ORDER: PurchaseStage[] = [
  'vendor_select',
  'item_select',
  'price_confirm',
  'delivery_schedule',
  'iqc_rule',
  'receiving',
  'invoice_input',
  'ap_completion',
  'close',
];

export const STAGE_LABELS: Record<PurchaseStage, { en: string; zh: string }> = {
  vendor_select:     { en: 'Vendor Select',     zh: '選擇供應商' },
  item_select:       { en: 'Item Select',       zh: '選擇品項' },
  price_confirm:     { en: 'Price Confirm',     zh: '確認價格' },
  delivery_schedule: { en: 'Delivery Schedule', zh: '交期安排' },
  iqc_rule:          { en: 'IQC Rule',          zh: '檢驗規則' },
  receiving:         { en: 'Receiving',         zh: '收貨入庫' },
  invoice_input:     { en: 'Invoice Input',     zh: '發票輸入' },
  ap_completion:     { en: 'AP Completion',     zh: '付款完成' },
  close:             { en: 'Close',             zh: '結案' },
};

export interface PurchaseFlow {
  flow_id: string;
  flow_button_id?: string;
  current_stage: PurchaseStage;
  stage_data: Record<string, unknown>;
  stage_history: StageTransition[];
  vendor_code?: string;
  po_id?: string;
  inbound_ids?: string[];
  invoice_id?: string;
  status: FlowStatus;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface StageTransition {
  from: PurchaseStage;
  to: PurchaseStage;
  action: string;
  username: string;
  timestamp: string;
  notes?: string;
}

export interface FlowListResponse {
  flows: PurchaseFlow[];
  total: number;
  limit: number;
  offset: number;
}

// ============================================================================
// Agent Token Types (mirrors sdkToken.ts for SSE streaming)
// ============================================================================

export enum AgentTokenType {
  TextDelta    = 1,
  TextDone     = 2,
  ToolStart    = 3,
  ToolDone     = 5,
  TurnComplete = 6,
  Error        = 8,
}

export interface AgentToken {
  type: AgentTokenType;
  ts: number;
  data?: Record<string, unknown>;
}

// ============================================================================
// Chat Message Types (assembled from token stream)
// ============================================================================

export type ChatRole = 'user' | 'assistant' | 'stage_banner';

export interface ChatMessage {
  id: number;
  role: ChatRole;
  content: string;
  toolCalls?: ToolCallInfo[];
  richBlocks?: RichBlock[];
  quickReplies?: QuickReply[];
  isStreaming?: boolean;
}

export interface ToolCallInfo {
  toolId: string;
  toolName: string;
  args?: string;
  result?: string;
  done: boolean;
  timestamp?: number;
}

export interface QuickReply {
  label: string;
  value: string;
}

// ============================================================================
// Rich Block Types (embedded UI components in chat messages)
// ============================================================================

export type RichBlockType =
  | 'vendor_list'
  | 'vendor_created'
  | 'product_list'
  | 'product_created'
  | 'quotation_list'
  | 'price_tier_table'
  | 'po_summary'
  | 'inbound_summary'
  | 'invoice_summary'
  | 'match_table'
  | 'flow_checklist'
  | 'create_vendor_form'
  | 'part_picker'
  | 'delivery_date'
  | 'delivery_address'
  | 'payment_terms';

export interface RichBlock {
  type: RichBlockType;
  data: unknown;
  toolCallId: string;
}

// Tool result → block type mapping
export const TOOL_BLOCK_MAPPING: Record<string, RichBlockType> = {
  psi_list_vendors:          'vendor_list',
  psi_create_vendor:         'vendor_created',
  psi_search_products:       'product_list',
  psi_create_product:        'product_created',
  psi_search_quotations:     'quotation_list',
  psi_get_price_tiers:       'price_tier_table',
  psi_show_calendar:         'delivery_date',
  psi_list_addresses:        'delivery_address',
  psi_list_payment_terms:    'payment_terms',
  psi_create_purchase_order: 'po_summary',
  psi_create_inbound:        'inbound_summary',
  psi_create_invoice:        'invoice_summary',
  psi_three_way_match:       'match_table',
  psi_flow_summary:          'flow_checklist',
  psi_create_vendor_form:    'create_vendor_form',
  psi_open_part_picker:      'part_picker',
};

// ============================================================================
// Activity Entry (for Activity tab in ContextPanel)
// ============================================================================

export interface ActivityEntry {
  toolId: string;
  toolName: string;
  timestamp: number;
  args?: Record<string, unknown>;
  result?: Record<string, unknown>;
  done: boolean;
  isCreate?: boolean; // highlight PO/inbound/invoice creation
}

// ============================================================================
// Mock Data Types (for rich block rendering)
// ============================================================================

export interface MockVendor {
  vendor_code: string;
  name: string;
  tax_id: string;
  contact: string;
  phone: string;
  email: string;
}

export interface MockProduct {
  part_code: string;
  description: string;
  specification: string;
  vendor_code: string;
  vendor_name: string;
  last_purchase_price: number;
  currency: string;
  lead_time_days: number;
  moq: number;
}

export interface MockPOSummary {
  po_id: string;
  po_number: string;
  vendor_code: string;
  vendor_name: string;
  items: { product_name: string; specification: string; quantity: number; unit_price: number; currency: string }[];
  subtotal: number;
  tax_amount: number;
  grand_total: number;
  currency: string;
  status: string;
}

export interface MockInboundSummary {
  inbound_id: string;
  inbound_number: string;
  po_number: string;
  vendor_name: string;
  received_date: string;
  items: { line_no: number; product_name: string; received_qty: number; po_qty: number; iqc_status: string }[];
  total_amount: number;
  currency: string;
  status: string;
}

export interface MockInvoiceSummary {
  invoice_id: string;
  invoice_number: string;
  vendor_name: string;
  invoice_date: string;
  due_date: string;
  grand_total: number;
  currency: string;
  status: string;
  items: { product_name: string; quantity: number; unit_price: number; amount: number }[];
}

export interface MockMatchResult {
  po_number: string;
  overall_match: boolean;
  lines: { product_name: string; po_qty: number; received_qty: number; invoiced_qty: number; po_amount: number; invoice_amount: number; match: boolean }[];
  po_total: number;
  inbound_total: number;
  invoice_total: number;
}

export interface MockFlowChecklist {
  flow_id: string;
  po_number: string;
  vendor_name: string;
  checklist: Record<string, boolean>;
  totals: { po_total: number; received_total: number; invoiced_total: number };
  currency: string;
}

export interface PriceTier {
  min_qty: number;
  unit_price: number;
}

export interface PriceTierItem {
  part_code: string;
  product_name: string;
  specification: string;
  current_qty: number;
  current_price: number;
  currency: string;
  tiers: PriceTier[];
}

export interface PriceTierTableData {
  items: PriceTierItem[];
  vendor_code: string;
  vendor_name: string;
}

// PSI tool display name mapping
export const PSI_TOOL_LABELS: Record<string, string> = {
  psi_list_vendors:          'Searched vendors',
  psi_create_vendor:         'Created vendor',
  psi_search_products:       'Searched products',
  psi_create_product:        'Created product',
  psi_create_purchase_order: 'Created PO',
  psi_update_purchase_order: 'Updated PO',
  psi_update_po_status:      'Updated PO status',
  psi_get_purchase_order:    'Loaded PO details',
  psi_list_purchase_orders:  'Listed POs',
  psi_search_quotations:     'Searched quotations',
  psi_get_price_tiers:       'Loaded price tiers',
  psi_list_vendor_parts:     'Compared vendor AVL',
  psi_show_calendar:         'Opened delivery calendar',
  psi_list_addresses:        'Loaded address book',
  psi_list_payment_terms:    'Loaded payment terms',
  psi_create_quotation:      'Created quotation',
  psi_create_inbound:        'Recorded delivery',
  psi_update_iqc:            'Updated IQC results',
  psi_list_inbounds:         'Listed shipments',
  psi_create_invoice:        'Created invoice',
  psi_update_invoice_status: 'Updated invoice status',
  psi_three_way_match:       'Matched PO/Inbound/Invoice',
  psi_flow_get_status:       'Checked flow status',
  psi_flow_advance_stage:    'Advanced stage',
  psi_flow_go_back:          'Returned to previous stage',
  psi_flow_summary:          'Generated summary',
};

// Tools that represent document creation (highlighted in activity log)
export const CREATE_TOOLS = new Set([
  'psi_create_vendor',
  'psi_create_product',
  'psi_create_purchase_order',
  'psi_create_inbound',
  'psi_create_invoice',
  'psi_create_quotation',
]);
